import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Vote, CheckCircle, XCircle, Clock, Users, ThumbsUp, ThumbsDown,
  Plus, Shield, AlertTriangle, Loader2, Settings, ArrowRight,
  Zap, Lock, Play, RefreshCw, UserCheck, UserMinus, BarChart3
} from 'lucide-react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { formatNumber } from '@/lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  active:   { label: 'Active',   color: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
  passed:   { label: 'Passed',   color: 'bg-green-500/10 text-green-500 border-green-500/20' },
  rejected: { label: 'Rejected', color: 'bg-red-500/10 text-red-500 border-red-500/20' },
  queued:   { label: 'Queued',   color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20' },
  executed: { label: 'Executed', color: 'bg-purple-500/10 text-purple-500 border-purple-500/20' },
};

const TYPE_CONFIG = {
  parameter_change:  { label: 'Parameter Change',  icon: Settings, color: 'text-blue-400' },
  treasury_transfer: { label: 'Treasury Transfer', icon: ArrowRight, color: 'text-green-400' },
  protocol_upgrade:  { label: 'Protocol Upgrade',  icon: Zap, color: 'text-orange-400' },
  general:           { label: 'General Signal',    icon: Vote, color: 'text-gray-400' },
};

export default function Governance() {
  const { token, user } = useAuth();
  const { toast } = useToast();
  const [proposals, setProposals] = useState([]);
  const [govStats, setGovStats] = useState(null);
  const [votingPower, setVotingPower] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [voteDialogOpen, setVoteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [delegateDialogOpen, setDelegateDialogOpen] = useState(false);
  const [delegateId, setDelegateId] = useState('');
  const [userVotes, setUserVotes] = useState({});
  const [newProposal, setNewProposal] = useState({
    title: '', description: '', category: 'Feature',
    proposal_type: 'general', execution_params: {},
  });
  const [paramKey, setParamKey] = useState('');
  const [paramValue, setParamValue] = useState('');
  const [treasuryRecipient, setTreasuryRecipient] = useState('');
  const [treasuryAmount, setTreasuryAmount] = useState('');

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const refresh = useCallback(async () => {
    try {
      const [pRes, sRes] = await Promise.all([
        axios.get(`${API}/api/governance/proposals`),
        axios.get(`${API}/api/governance/stats`),
      ]);
      setProposals((pRes.data.proposals || []).map(p => ({
        ...p,
        votesFor: p.votes_for || 0,
        votesAgainst: p.votes_against || 0,
        totalVotes: p.total_votes || 0,
        proposer: p.proposer_name || 'Anonymous',
        endDate: p.end_date,
      })));
      setGovStats(sRes.data);
    } catch (e) {
      console.error('Governance fetch error:', e);
    } finally {
      setFetchLoading(false);
    }
  }, []);

  const fetchVotingPower = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/governance/voting-power-me`, { headers });
      setVotingPower(res.data);
    } catch { setVotingPower({ own_stake: 0, delegated_received: 0, delegated_away: 0, total: 0 }); }
  }, [token]);

  useEffect(() => { refresh(); fetchVotingPower(); }, [refresh, fetchVotingPower]);

  const activeProposals = proposals.filter(p => p.status === 'active');
  const queuedProposals = proposals.filter(p => p.status === 'queued');
  const closedProposals = proposals.filter(p => ['passed', 'rejected', 'executed'].includes(p.status));

  const handleVote = async (proposalId, support) => {
    if (!token) { toast({ title: 'Login Required', variant: 'destructive' }); return; }
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/governance/proposals/${proposalId}/vote`,
        { support },
        { headers }
      );
      setUserVotes(prev => ({ ...prev, [proposalId]: support }));
      toast({ title: 'Vote Cast!', description: res.data.message });
      setVoteDialogOpen(false);
      await refresh();
    } catch (err) {
      toast({ title: 'Vote Failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!newProposal.title || !newProposal.description) {
      toast({ title: 'Error', description: 'Title and description required', variant: 'destructive' });
      return;
    }
    if (!token) { toast({ title: 'Login Required', variant: 'destructive' }); return; }

    const payload = { ...newProposal };
    if (payload.proposal_type === 'parameter_change') {
      payload.execution_params = { param_key: paramKey, new_value: parseFloat(paramValue) };
    } else if (payload.proposal_type === 'treasury_transfer') {
      payload.execution_params = { recipient: treasuryRecipient, amount: parseFloat(treasuryAmount) };
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/governance/proposals`, payload, { headers });
      toast({ title: 'Proposal Created!', description: `${res.data.id} submitted` });
      setCreateDialogOpen(false);
      setNewProposal({ title: '', description: '', category: 'Feature', proposal_type: 'general', execution_params: {} });
      setParamKey(''); setParamValue(''); setTreasuryRecipient(''); setTreasuryAmount('');
      await refresh();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const handleQueue = async (proposalId) => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/governance/proposals/${proposalId}/queue`, {}, { headers });
      toast({ title: 'Queued!', description: res.data.message });
      await refresh();
    } catch (err) {
      toast({ title: 'Queue Failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const handleExecute = async (proposalId) => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/governance/proposals/${proposalId}/execute`, {}, { headers });
      toast({ title: 'Executed!', description: res.data.message });
      setDetailDialogOpen(false);
      await refresh();
    } catch (err) {
      toast({ title: 'Execution Failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const handleDelegate = async () => {
    if (!token || !delegateId.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/governance/delegate`, { delegate_id: delegateId.trim() }, { headers });
      toast({ title: 'Delegated!', description: res.data.message });
      setDelegateDialogOpen(false);
      setDelegateId('');
      await fetchVotingPower();
    } catch (err) {
      toast({ title: 'Delegation Failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const handleUndelegate = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/governance/undelegate`, {}, { headers });
      toast({ title: 'Undelegated', description: res.data.message });
      await fetchVotingPower();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const getRemainingTime = (endDate) => {
    const remaining = new Date(endDate) - new Date();
    if (remaining <= 0) return 'Ended';
    const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
    const hours = Math.floor((remaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    return days > 0 ? `${days}d ${hours}h remaining` : `${hours}h remaining`;
  };

  const ProposalCard = ({ proposal }) => {
    const forPct = proposal.totalVotes > 0 ? (proposal.votesFor / proposal.totalVotes) * 100 : 0;
    const quorumPct = Math.min(100, (proposal.totalVotes / proposal.quorum) * 100);
    const hasVoted = userVotes[proposal.id] !== undefined;
    const isActive = proposal.status === 'active';
    const typeConf = TYPE_CONFIG[proposal.proposal_type] || TYPE_CONFIG.general;
    const TypeIcon = typeConf.icon;
    const statusConf = STATUS_CONFIG[proposal.status] || STATUS_CONFIG.active;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ scale: 1.005 }}
        className="cursor-pointer"
        onClick={() => { setSelectedProposal(proposal); isActive && !hasVoted ? setVoteDialogOpen(true) : setDetailDialogOpen(true); }}
        data-testid={`proposal-card-${proposal.id}`}
      >
        <Card className="glass border-white/10 hover:border-primary/30 transition-all">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Badge variant="outline" className="font-mono">{proposal.id}</Badge>
                  <Badge className={`border ${statusConf.color}`}>{statusConf.label}</Badge>
                  <Badge variant="outline" className={`flex items-center gap-1 ${typeConf.color}`}>
                    <TypeIcon className="w-3 h-3" />{typeConf.label}
                  </Badge>
                </div>
                <h3 className="text-lg font-semibold mb-2">{proposal.title}</h3>
                <p className="text-sm text-muted-foreground line-clamp-2">{proposal.description}</p>
              </div>
            </div>

            {/* Execution Params Summary */}
            {proposal.proposal_type === 'parameter_change' && proposal.execution_params?.param_key && (
              <div className="mb-4 p-3 rounded-lg bg-blue-500/5 border border-blue-500/10 text-sm">
                <span className="text-blue-400 font-mono">{proposal.execution_params.param_key}</span>
                <span className="text-gray-500 mx-2">{proposal.execution_params.current_value} →</span>
                <span className="text-white font-bold">{proposal.execution_params.new_value}</span>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="flex items-center gap-1 text-green-500">
                    <ThumbsUp className="w-4 h-4" /> {formatNumber(proposal.votesFor)} ({forPct.toFixed(1)}%)
                  </span>
                  <span className="flex items-center gap-1 text-red-500">
                    <ThumbsDown className="w-4 h-4" /> {formatNumber(proposal.votesAgainst)} ({(100 - forPct).toFixed(1)}%)
                  </span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-green-500 to-green-400" style={{ width: `${forPct}%` }} />
                </div>
              </div>

              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Quorum</span>
                  <span className={quorumPct >= 100 ? 'text-green-500' : 'text-muted-foreground'}>
                    {formatNumber(proposal.totalVotes)} / {formatNumber(proposal.quorum)} KOJIRU
                  </span>
                </div>
                <Progress value={quorumPct} className="h-1" />
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-white/10">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="w-4 h-4" /> {proposal.proposer}
                </div>
                <div className="flex items-center gap-2">
                  {isActive && <span className="text-sm text-muted-foreground flex items-center gap-1"><Clock className="w-4 h-4" />{getRemainingTime(proposal.endDate)}</span>}
                  {proposal.status === 'passed' && (
                    <Button size="sm" onClick={(e) => { e.stopPropagation(); handleQueue(proposal.id); }}
                      className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 text-xs" data-testid={`queue-${proposal.id}`}>
                      <Lock className="w-3 h-3 mr-1" /> Queue
                    </Button>
                  )}
                  {proposal.status === 'queued' && (
                    <Button size="sm" onClick={(e) => { e.stopPropagation(); handleExecute(proposal.id); }}
                      className="bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 text-xs" data-testid={`execute-${proposal.id}`}>
                      <Play className="w-3 h-3 mr-1" /> Execute
                    </Button>
                  )}
                  {proposal.status === 'executed' && (
                    <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/20"><CheckCircle className="w-3 h-3 mr-1" /> Executed</Badge>
                  )}
                  {hasVoted && <Badge className="bg-primary/20 text-primary"><CheckCircle className="w-3 h-3 mr-1" /> Voted</Badge>}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  if (fetchLoading) return <DashboardLayout title="Governance"><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-gray-500" /></div></DashboardLayout>;

  return (
    <DashboardLayout title="Governance">
      <div className="max-w-6xl mx-auto space-y-6" data-testid="governance-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold mb-1" data-testid="governance-title">KOJIRU DAO Governance</h2>
            <p className="text-muted-foreground">Propose, vote, queue, and execute protocol changes on-chain.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Card className="glass border-white/10 px-4 py-2">
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Your Voting Power</div>
              <div className="text-xl font-bold gradient-text" data-testid="voting-power-display">
                {votingPower ? `${formatNumber(votingPower.total)} KOJIRU` : 'Login to view'}
              </div>
              {votingPower && votingPower.delegated_received > 0 && (
                <div className="text-[10px] text-green-400">+{formatNumber(votingPower.delegated_received)} delegated</div>
              )}
              {votingPower && votingPower.delegated_away > 0 && (
                <div className="text-[10px] text-orange-400">-{formatNumber(votingPower.delegated_away)} delegated out</div>
              )}
            </Card>
            <div className="flex flex-col gap-1">
              <Button onClick={() => setDelegateDialogOpen(true)} variant="outline" size="sm" className="text-xs border-white/10" data-testid="delegate-btn" disabled={!token}>
                <UserCheck className="w-3 h-3 mr-1" /> Delegate
              </Button>
              {votingPower?.delegated_away > 0 && (
                <Button onClick={handleUndelegate} variant="outline" size="sm" className="text-xs border-orange-500/20 text-orange-400" data-testid="undelegate-btn">
                  <UserMinus className="w-3 h-3 mr-1" /> Undelegate
                </Button>
              )}
            </div>
            <Button onClick={() => setCreateDialogOpen(true)} className="gradient-primary" disabled={!token} data-testid="create-proposal-btn">
              <Plus className="w-4 h-4 mr-2" /> Create Proposal
            </Button>
          </div>
        </div>

        {/* Stats Row */}
        {govStats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="governance-stats">
            {[
              { label: 'Proposals', value: govStats.total_proposals, icon: Vote, color: 'text-blue-400' },
              { label: 'Active', value: govStats.active, icon: Clock, color: 'text-blue-400' },
              { label: 'Passed', value: govStats.passed, icon: CheckCircle, color: 'text-green-400' },
              { label: 'Executed', value: govStats.executed, icon: Play, color: 'text-purple-400' },
              { label: 'Votes Cast', value: formatNumber(govStats.total_votes_cast), icon: ThumbsUp, color: 'text-orange-400' },
              { label: 'Delegations', value: govStats.active_delegations, icon: UserCheck, color: 'text-cyan-400' },
            ].map(s => (
              <Card key={s.label} className="glass border-white/10">
                <CardContent className="p-4 flex items-center gap-3">
                  <s.icon className={`w-5 h-5 ${s.color}`} />
                  <div><div className="text-lg font-mono font-bold">{s.value}</div><div className="text-[10px] text-muted-foreground uppercase">{s.label}</div></div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!token && (
          <Card className="glass border-yellow-500/30">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              <p className="text-sm">Log in to view your voting power, vote on proposals, and participate in governance. Voting power = staked KOJIRU + delegated power.</p>
            </CardContent>
          </Card>
        )}

        {/* Proposals */}
        <Tabs defaultValue="active" className="w-full">
          <TabsList className="glass border-white/10">
            <TabsTrigger value="active" className="data-[state=active]:bg-primary/20">Active ({activeProposals.length})</TabsTrigger>
            <TabsTrigger value="queued" className="data-[state=active]:bg-primary/20">Queued ({queuedProposals.length})</TabsTrigger>
            <TabsTrigger value="closed" className="data-[state=active]:bg-primary/20">Closed ({closedProposals.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="active" className="mt-6">
            {activeProposals.length === 0 ? (
              <Card className="glass border-white/10"><CardContent className="py-12 text-center">
                <Vote className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-xl font-semibold mb-2">No Active Proposals</h3>
                <p className="text-muted-foreground mb-4">There are no proposals currently open for voting.</p>
                <Button onClick={() => setCreateDialogOpen(true)} className="gradient-primary" data-testid="create-first-proposal-btn">Create the First Proposal</Button>
              </CardContent></Card>
            ) : (
              <div className="grid gap-4">{activeProposals.map(p => <ProposalCard key={p.id} proposal={p} />)}</div>
            )}
          </TabsContent>

          <TabsContent value="queued" className="mt-6">
            {queuedProposals.length === 0 ? (
              <Card className="glass border-white/10"><CardContent className="py-12 text-center text-muted-foreground">No proposals in queue.</CardContent></Card>
            ) : (
              <div className="grid gap-4">{queuedProposals.map(p => <ProposalCard key={p.id} proposal={p} />)}</div>
            )}
          </TabsContent>

          <TabsContent value="closed" className="mt-6">
            <div className="grid gap-4">{closedProposals.map(p => <ProposalCard key={p.id} proposal={p} />)}</div>
          </TabsContent>
        </Tabs>

        {/* Recent Executions */}
        {govStats?.recent_executions?.length > 0 && (
          <Card className="glass border-white/10" data-testid="execution-log">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-purple-400" /> Recent Executions</h3>
              <div className="space-y-3">
                {govStats.recent_executions.map(ex => (
                  <div key={ex.execution_id} className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03] border border-white/5">
                    <div>
                      <span className="text-sm font-mono font-bold text-purple-400">{ex.proposal_id}</span>
                      <span className="text-xs text-muted-foreground ml-2">{ex.proposal_type}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">{new Date(ex.executed_at).toLocaleDateString()}</div>
                      <div className="text-[10px] font-mono text-gray-600">{ex.proof_hash?.slice(0, 16)}...</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Vote Dialog */}
        <Dialog open={voteDialogOpen} onOpenChange={setVoteDialogOpen}>
          <DialogContent className="glass border-white/10">
            <DialogHeader>
              <DialogTitle>Cast Your Vote</DialogTitle>
              <DialogDescription>{selectedProposal?.id}: {selectedProposal?.title}</DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-muted-foreground mb-4">{selectedProposal?.description}</p>
              {selectedProposal?.proposal_type === 'parameter_change' && selectedProposal?.execution_params?.param_key && (
                <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-sm">
                  <div className="text-xs text-blue-400 uppercase mb-1">Proposed Change</div>
                  <span className="font-mono">{selectedProposal.execution_params.param_key}</span>
                  <span className="text-gray-500 mx-2">: {selectedProposal.execution_params.current_value} →</span>
                  <span className="text-white font-bold">{selectedProposal.execution_params.new_value}</span>
                </div>
              )}
              <div className="p-4 rounded-xl bg-white/5 mb-4">
                <div className="text-sm text-muted-foreground mb-1">Your Voting Power</div>
                <div className="text-xl font-bold">{votingPower ? `${formatNumber(votingPower.total)} KOJIRU` : '—'}</div>
              </div>
            </div>
            <DialogFooter className="flex gap-3">
              <Button variant="outline" onClick={() => handleVote(selectedProposal?.id, false)} disabled={loading}
                className="flex-1 border-red-500/50 text-red-500 hover:bg-red-500/10" data-testid="vote-against-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsDown className="w-4 h-4 mr-2" />} Vote Against
              </Button>
              <Button onClick={() => handleVote(selectedProposal?.id, true)} disabled={loading}
                className="flex-1 bg-green-500 hover:bg-green-600" data-testid="vote-for-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsUp className="w-4 h-4 mr-2" />} Vote For
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail / Execute Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="glass border-white/10 max-w-lg">
            <DialogHeader>
              <DialogTitle>{selectedProposal?.id}: {selectedProposal?.title}</DialogTitle>
              <DialogDescription className="flex items-center gap-2 pt-2">
                <Badge className={`border ${STATUS_CONFIG[selectedProposal?.status]?.color || ''}`}>{STATUS_CONFIG[selectedProposal?.status]?.label}</Badge>
                <Badge variant="outline">{TYPE_CONFIG[selectedProposal?.proposal_type]?.label || 'General'}</Badge>
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4 max-h-[60vh] overflow-y-auto">
              <p className="text-sm text-muted-foreground">{selectedProposal?.description}</p>

              {selectedProposal?.execution_params && Object.keys(selectedProposal.execution_params).length > 0 && (
                <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/10">
                  <div className="text-xs text-blue-400 uppercase mb-2">Execution Parameters</div>
                  {Object.entries(selectedProposal.execution_params).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm py-1">
                      <span className="text-gray-500">{k}</span><span className="font-mono">{String(v)}</span>
                    </div>
                  ))}
                </div>
              )}

              {selectedProposal?.execution_result && (
                <div className="p-3 rounded-lg bg-purple-500/5 border border-purple-500/10">
                  <div className="text-xs text-purple-400 uppercase mb-2">Execution Result</div>
                  {Object.entries(selectedProposal.execution_result).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm py-1">
                      <span className="text-gray-500">{k}</span><span className="font-mono text-xs">{String(v)}</span>
                    </div>
                  ))}
                  {selectedProposal.execution_proof && (
                    <div className="mt-2 text-[10px] font-mono text-gray-600">Proof: {selectedProposal.execution_proof}</div>
                  )}
                </div>
              )}

              {/* Vote breakdown */}
              {selectedProposal?.vote_details?.length > 0 && (
                <div>
                  <div className="text-xs text-muted-foreground uppercase mb-2">Vote Breakdown</div>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {selectedProposal.vote_details.map((v, i) => (
                      <div key={i} className="flex items-center justify-between text-sm p-2 rounded bg-white/[0.03]">
                        <span className="text-muted-foreground">{v.voter_name}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs">{formatNumber(v.voting_power)}</span>
                          {v.support ? <ThumbsUp className="w-3 h-3 text-green-400" /> : <ThumbsDown className="w-3 h-3 text-red-400" />}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              {selectedProposal?.status === 'passed' && (
                <Button onClick={() => handleQueue(selectedProposal.id)} disabled={loading} className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30" data-testid="dialog-queue-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Lock className="w-4 h-4 mr-1" />} Queue for Execution
                </Button>
              )}
              {selectedProposal?.status === 'queued' && (
                <Button onClick={() => handleExecute(selectedProposal.id)} disabled={loading} className="bg-purple-500 hover:bg-purple-600" data-testid="dialog-execute-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />} Execute Proposal
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Proposal Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="glass border-white/10 max-w-lg">
            <DialogHeader>
              <DialogTitle>Create New Proposal</DialogTitle>
              <DialogDescription>Submit a governance proposal for the KOJIRU DAO</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div>
                <Label>Proposal Type</Label>
                <select
                  value={newProposal.proposal_type}
                  onChange={e => setNewProposal({ ...newProposal, proposal_type: e.target.value })}
                  className="w-full mt-1.5 bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm"
                  data-testid="proposal-type-select"
                >
                  {Object.entries(TYPE_CONFIG).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Title</Label>
                <Input data-testid="proposal-title-input" placeholder="Enter proposal title" value={newProposal.title}
                  onChange={e => setNewProposal({ ...newProposal, title: e.target.value })} className="mt-1.5 bg-white/5 border-white/10" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea data-testid="proposal-description-input" placeholder="Describe your proposal..." value={newProposal.description}
                  onChange={e => setNewProposal({ ...newProposal, description: e.target.value })} className="mt-1.5 bg-white/5 border-white/10" rows={4} />
              </div>

              {newProposal.proposal_type === 'parameter_change' && (
                <div className="p-4 rounded-xl bg-blue-500/5 border border-blue-500/10 space-y-3">
                  <div className="text-xs text-blue-400 uppercase font-semibold">Parameter Change</div>
                  <div>
                    <Label className="text-xs">Parameter Key</Label>
                    <select value={paramKey} onChange={e => setParamKey(e.target.value)}
                      className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono"
                      data-testid="param-key-select">
                      <option value="">Select parameter...</option>
                      {govStats?.current_params && Object.entries(govStats.current_params).map(([k, v]) => (
                        <option key={k} value={k}>{k} (current: {v})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">New Value</Label>
                    <Input data-testid="param-value-input" type="number" step="any" placeholder="New value" value={paramValue}
                      onChange={e => setParamValue(e.target.value)} className="mt-1 bg-white/5 border-white/10 font-mono" />
                  </div>
                </div>
              )}

              {newProposal.proposal_type === 'treasury_transfer' && (
                <div className="p-4 rounded-xl bg-green-500/5 border border-green-500/10 space-y-3">
                  <div className="text-xs text-green-400 uppercase font-semibold">Treasury Transfer</div>
                  <div>
                    <Label className="text-xs">Recipient Address</Label>
                    <Input data-testid="treasury-recipient-input" placeholder="0x..." value={treasuryRecipient}
                      onChange={e => setTreasuryRecipient(e.target.value)} className="mt-1 bg-white/5 border-white/10 font-mono" />
                  </div>
                  <div>
                    <Label className="text-xs">Amount (USD)</Label>
                    <Input data-testid="treasury-amount-input" type="number" placeholder="10000" value={treasuryAmount}
                      onChange={e => setTreasuryAmount(e.target.value)} className="mt-1 bg-white/5 border-white/10 font-mono" />
                  </div>
                </div>
              )}

              <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                <div className="flex items-start gap-2">
                  <Shield className="w-5 h-5 text-yellow-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-500">Proposal Requirements</p>
                    <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                      <li>Minimum {govStats?.current_params?.min_proposal_stake || 500} KOJIRU staked</li>
                      <li>Quorum: {formatNumber(govStats?.current_params?.quorum_kutro || 5000000)} KOJIRU</li>
                      <li>Voting period: {govStats?.current_params?.voting_period_days || 7} days</li>
                      <li>Timelock: {govStats?.current_params?.timelock_hours || 48}h after passing</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={loading} className="gradient-primary" data-testid="submit-proposal-btn">
                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Submit Proposal
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delegate Dialog */}
        <Dialog open={delegateDialogOpen} onOpenChange={setDelegateDialogOpen}>
          <DialogContent className="glass border-white/10">
            <DialogHeader>
              <DialogTitle>Delegate Voting Power</DialogTitle>
              <DialogDescription>Transfer your voting power to another user. You can undelegate at any time.</DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="p-4 rounded-xl bg-white/5">
                <div className="text-sm text-muted-foreground mb-1">Your Staked KOJIRU</div>
                <div className="text-xl font-bold">{votingPower ? formatNumber(votingPower.own_stake) : '—'}</div>
              </div>
              <div>
                <Label>Delegate To (User ID)</Label>
                <Input data-testid="delegate-id-input" placeholder="User ID of the delegate" value={delegateId}
                  onChange={e => setDelegateId(e.target.value)} className="mt-1.5 bg-white/5 border-white/10 font-mono" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDelegateDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleDelegate} disabled={loading} className="gradient-primary" data-testid="confirm-delegate-btn">
                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Delegate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
